import React from "react";
import styled from "styled-components";
import {
  LuBrain,
  LuCheck,
  LuCodeXml,
  LuCpu,
  LuServerCog,
  LuSmartphone,
} from "react-icons/lu";
import {
  Container,
  IconBox,
  Reveal,
  Section,
  SectionHeader,
  Tag,
  TagList,
} from "./ui";

const services = [
  {
    icon: <LuBrain />,
    title: "AI, ML & Deep Learning",
    description:
      "Artificial intelligence, machine learning and deep learning solutions tailored to your data and your business goals.",
    features: [
      "Custom AI model development & training",
      "Natural language processing (NLP)",
      "Computer vision & image recognition",
      "Predictive analytics & forecasting",
      "Recommendation systems",
      "AI chatbots & virtual assistants",
      "Sentiment analysis & text mining",
      "AI-powered automation",
      "Model deployment & MLOps",
      "Data science consulting",
    ],
    technologies: [
      "TensorFlow",
      "PyTorch",
      "Scikit-learn",
      "Keras",
      "OpenCV",
      "Hugging Face",
      "LangChain",
      "Python",
      "FastAPI",
      "NLTK",
      "spaCy",
      "Pandas",
    ],
  },
  {
    icon: <LuCodeXml />,
    title: "Web Development",
    description:
      "Modern, responsive web applications built with proven technologies and engineering best practices.",
    features: [
      "Responsive, mobile-first design",
      "Progressive web apps (PWA)",
      "Single-page applications (SPA)",
      "E-commerce solutions",
      "CMS integration",
      "API development & integration",
    ],
    technologies: [
      "React",
      "Next.js",
      "Vue.js",
      "Node.js",
      "Express",
      "Spring Boot",
      "JEE",
      ".NET",
      "MongoDB",
      "PostgreSQL",
      "SQL Server",
    ],
  },
  {
    icon: <LuSmartphone />,
    title: "Mobile Development",
    description:
      "Cross-platform mobile applications that deliver native performance and a polished user experience.",
    features: [
      "Cross-platform development",
      "Native performance optimization",
      "Offline functionality",
      "Push notifications",
      "App Store & Play Store deployment",
      "Maintenance & updates",
    ],
    technologies: ["React Native", "Flutter", "Expo", "Firebase", "Redux", "SQLite"],
  },
  {
    icon: <LuCpu />,
    title: "IoT Integration",
    description:
      "Connect and control smart devices with robust IoT solutions and real-time data processing.",
    features: [
      "Device communication protocols",
      "Real-time data processing",
      "Sensor integration",
      "Remote monitoring systems",
      "Smart home solutions",
      "Industrial IoT applications",
    ],
    technologies: ["MQTT", "Arduino", "Raspberry Pi", "LoRaWAN", "Zigbee", "WebSocket"],
  },
  {
    icon: <LuServerCog />,
    title: "DevOps Consulting",
    description:
      "Streamline your delivery workflow with modern DevOps practices and cloud infrastructure.",
    features: [
      "CI/CD pipeline setup",
      "Cloud infrastructure management",
      "Containerization & orchestration",
      "Monitoring & logging",
      "Security & compliance",
      "Performance optimization",
    ],
    technologies: ["AWS", "Docker", "Kubernetes", "Jenkins", "Terraform", "Grafana"],
  },
];

const Panel = styled.ol`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  list-style: none;
`;

const Row = styled(Reveal)`
  display: grid;
  grid-template-columns: 56px minmax(0, 5fr) minmax(0, 6fr);
  gap: 32px;
  padding: 40px;

  & + & {
    border-top: 1px solid var(--color-border);
  }

  @media (max-width: 960px) {
    grid-template-columns: 56px minmax(0, 1fr);

    > :last-child {
      grid-column: 2;
    }
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 28px 20px;

    > :last-child {
      grid-column: auto;
    }
  }
`;

const Index = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  font-family: var(--font-mono);
  font-size: 0.8125rem;
  color: var(--color-muted);

  @media (max-width: 640px) {
    flex-direction: row-reverse;
    justify-content: flex-end;
    align-items: center;
  }
`;

const Title = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 12px;
`;

const Description = styled.p`
  color: var(--color-muted);
  margin-bottom: 24px;
`;

const Features = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 24px;
  align-content: start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.li`
  display: flex;
  gap: 10px;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--color-text);

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 2px;
    color: var(--color-primary);
  }
`;

const Services = () => (
  <Section id="services" $tone="subtle">
    <Container>
      <SectionHeader
        eyebrow="Services"
        title="What we can do for you"
        lead="From AI-powered systems to full-stack products, we cover the whole delivery chain: web, mobile, IoT and the DevOps that keeps it all running."
      />

      <Panel>
        {services.map((service, index) => (
          <Row key={service.title} forwardedAs="li">
            <Index>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <IconBox aria-hidden="true">{service.icon}</IconBox>
            </Index>

            <div>
              <Title>{service.title}</Title>
              <Description>{service.description}</Description>
              <TagList aria-label={`${service.title} technologies`}>
                {service.technologies.map(tech => (
                  <Tag key={tech}>{tech}</Tag>
                ))}
              </TagList>
            </div>

            <Features>
              {service.features.map(feature => (
                <Feature key={feature}>
                  <LuCheck aria-hidden="true" />
                  {feature}
                </Feature>
              ))}
            </Features>
          </Row>
        ))}
      </Panel>
    </Container>
  </Section>
);

export default Services;
